import { DatePicker } from '@syncfusion/ej2-calendars';
import { DropDownList } from '@syncfusion/ej2-dropdowns';
import { TextBox } from '@syncfusion/ej2-inputs';
import { Dialog } from '@syncfusion/ej2-popups';

import { routes } from '@/shared/routes';
import { AmlRiskLevel, AmlUpdateSource, ClientType } from '@/shared/types/enum';
import { parseIsoDateTime } from '@/shared/utils/date';
import { enumOptions } from '@/shared/utils/enum';
import { offcanvasUrl } from '@/shared/utils/url';
import { utcAddDays } from '@/shared/utils/utc';

import { POST } from '@/front/fetch';
import { alertModal } from '@/front/modal';

let dialog: Dialog | null = null;
let riskLevelDropdown: DropDownList | null = null;
let assessmentDatePicker: DatePicker | null = null;
let notesInput: TextBox | null = null;

let currentClientId: string | null = null;
let currentClientType: ClientType | null = null;
let currentRiskLevel: string | null = null;
let currentTaskId: string | null = null;
let currentAmlLastCheckedDate: string | null = null;
let onCompleteCallback: (() => void) | null = null;
let currentSkipRedirect: boolean = false;
let isSubmitting: boolean = false;

const buildContent = () => `
  <div class="d-flex flex-column gap-3">
    <div class="alert alert-info mb-0 small" role="alert">
      Recording this AML Risk Assessment will complete the current task and schedule the next AML task based on the configured renewal interval in account settings, the selected risk level, and the risk assessment date entered.
    </div>
    <div class="d-flex flex-column gap-1">
      <label class="form-label mb-0">Risk Level:</label>
      <input id="amlRiskAssessmentLevel" />
    </div>
    <div class="d-flex flex-column gap-1">
      <label class="form-label mb-0">Risk Assessment Date:</label>
      <input id="amlRiskAssessmentDate" />
    </div>
    <div class="d-flex flex-column gap-1">
      <label class="form-label mb-0">Notes:</label>
      <textarea id="amlRiskAssessmentNotes"></textarea>
    </div>
  </div>
`;

const initControls = () => {
  const maxDate = new Date();

  riskLevelDropdown = new DropDownList({
    dataSource: enumOptions(AmlRiskLevel),
    fields: { text: 'text', value: 'value' },
    value: AmlRiskLevel.Medium,
    popupHeight: '180px',
  });
  riskLevelDropdown.appendTo('#amlRiskAssessmentLevel');

  assessmentDatePicker = new DatePicker({
    value: maxDate,
    max: maxDate,
    showClearButton: false,
    format: 'dd/MM/yyyy',
  });
  assessmentDatePicker.appendTo('#amlRiskAssessmentDate');

  notesInput = new TextBox({
    placeholder: 'Enter additional notes here...',
    floatLabelType: 'Never',
    multiline: true,
  });
  notesInput.appendTo('#amlRiskAssessmentNotes');
};

const setButtonDisabled = (disabled: boolean) => {
  const button = document.querySelector<HTMLButtonElement>('#amlRiskAssessmentDialog button.e-primary');

  if (!button) return;

  button.disabled = disabled;
};

const completeRiskAssessment = async () => {
  if (isSubmitting) return;

  if (!currentClientId || !currentClientType || currentClientId.trim() === '') {
    alertModal('Missing client information. Please try again.');
    return;
  }

  if (!riskLevelDropdown || !assessmentDatePicker) {
    alertModal('Form not initialised. Please try again.');
    return;
  }

  const riskLevel = riskLevelDropdown.value as string;
  const assessmentDate = assessmentDatePicker.value;

  if (!riskLevel) {
    alertModal('Please select a risk level.');
    return;
  }

  if (!assessmentDate) {
    alertModal('Please select an assessment date.');
    return;
  }

  isSubmitting = true;

  setButtonDisabled(true);

  try {
    const url =
      currentClientType === ClientType.Organisation
        ? routes.organisations.amlUpdate({ id: currentClientId })
        : routes.individuals.amlUpdate({ id: currentClientId });

    const notes = notesInput?.value?.trim() || null;
    // todays date
    const dateAtNoon = new Date(assessmentDate);
    dateAtNoon.setHours(12, 0, 0, 0);
    const res = await POST(url, {
      amlRiskLevel: riskLevel,
      amlLastCheckedDate: dateAtNoon.toISOString(),
      amlNotes: notes,
      source: AmlUpdateSource.Modal,
      taskId: currentTaskId,
    });

    if (res.ok) {
      const data = await res.json();
      dialog?.hide();

      // skip redirect when called from grid view
      if (data.redirectUrl && !currentSkipRedirect) {
        const inOffcanvas = new URLSearchParams(window.location.search).get('offcanvas') === 'true';
        window.location.href = inOffcanvas ? offcanvasUrl(data.redirectUrl) : data.redirectUrl;
      } else if (onCompleteCallback) {
        onCompleteCallback();
      }
    } else {
      const data = await res.json();
      alertModal(data.message || 'Failed to complete risk assessment');
    }
  } catch (error) {
    console.error('Failed to complete risk assessment', error);
    alertModal('Failed to complete risk assessment. Please try again.');
  } finally {
    isSubmitting = false;
    setButtonDisabled(false);
  }
};

const ensureDialog = () => {
  if (dialog) {
    return dialog;
  }

  const host = document.createElement('div');
  host.id = 'amlRiskAssessmentDialog';
  document.body.appendChild(host);

  dialog = new Dialog({
    header: 'AML Risk Assessment',
    content: buildContent(),
    target: document.body,
    showCloseIcon: true,
    isModal: true,
    visible: false,
    width: '520px',
    zIndex: 1035,
    animationSettings: { effect: 'Fade' },
    buttons: [
      {
        click: () => dialog?.hide(),
        buttonModel: { content: 'Cancel', cssClass: 'e-btn e-flat' },
      },
      {
        click: completeRiskAssessment,
        buttonModel: { content: 'Record Risk Assessment', cssClass: 'e-btn e-primary' },
      },
    ],
    open: () => {
      isSubmitting = false;
      setButtonDisabled(false);

      if (!riskLevelDropdown || !assessmentDatePicker || !notesInput) {
        initControls();
      }

      if (!assessmentDatePicker || !riskLevelDropdown || !notesInput) {
        return;
      }

      const maxDate = new Date();
      assessmentDatePicker.max = maxDate;
      assessmentDatePicker.enabled = true;

      const parsed = currentAmlLastCheckedDate ? parseIsoDateTime(currentAmlLastCheckedDate) : null;
      assessmentDatePicker.min = parsed ? utcAddDays(new Date(parsed), 1) : new Date(0);
      assessmentDatePicker.value = maxDate;

      riskLevelDropdown.value =
        currentRiskLevel && Object.values(AmlRiskLevel).includes(currentRiskLevel as AmlRiskLevel)
          ? currentRiskLevel
          : AmlRiskLevel.Medium;
      notesInput.value = '';
    },
  });

  dialog.appendTo(host);
  return dialog;
};

export const isAmlAlreadyCompletedToday = (amlLastCheckedDate?: string | null): boolean => {
  const parsed = amlLastCheckedDate ? parseIsoDateTime(amlLastCheckedDate) : null;
  if (!parsed) return false;
  const nextAllowedDate = utcAddDays(parsed, 1);
  return nextAllowedDate > new Date();
};

export type AmlRiskAssessmentModalOptions = {
  clientId: string;
  clientType: ClientType;
  clientName?: string;
  taskId?: string;
  currentRiskLevel?: string | null;
  amlLastCheckedDate?: string | null;
  onComplete?: () => void;
  skipRedirect?: boolean;
};

export const openAmlRiskAssessmentModal = (options: AmlRiskAssessmentModalOptions) => {
  const {
    clientId,
    clientType,
    clientName,
    taskId,
    currentRiskLevel: riskLevel,
    amlLastCheckedDate,
    onComplete,
    skipRedirect,
  } = options;

  currentClientId = clientId;
  currentClientType = clientType;
  currentRiskLevel = riskLevel ?? null;
  currentTaskId = taskId ?? null;
  currentAmlLastCheckedDate = amlLastCheckedDate ?? null;
  onCompleteCallback = onComplete ?? null;
  currentSkipRedirect = skipRedirect ?? false;

  const dlg = ensureDialog();

  if (clientName) {
    dlg.header = `AML Risk Assessment (${clientName})`;
  } else {
    dlg.header = 'AML Risk Assessment';
  }

  dlg.show();
};
